import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { ENVIRONMENT, LoanProperty, LoanValidationResult } from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';
import { LoggingService } from '@ld.consumer-portal/utils';

@Injectable({
  providedIn: 'root',
})
export class LoanLinkingService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);

  readonly isLoading = signal(false);

  readonly error = signal<string | null>(null);

  readonly validationAttempts = signal(0);

  private get baseUrl(): string {
    return this.env.appSettings?.profileDomainUrl || '';
  }

  private get apiNamespace(): string {
    return this.env.appSettings?.apiNamespace || 'api/v1';
  }

  async validateLoan(
    loanNumber: string,
    zipCode: string,
    ssn: string,
    globalUserId: string
  ): Promise<LoanValidationResult> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      const url = `${this.baseUrl}/${
        this.apiNamespace
      }/LDProfileDomain/UserProfile/validateLoan/${loanNumber.trim()}/${ssn.trim()}/${zipCode.trim()}/${globalUserId}`;

      const result = await firstValueFrom(this.http.get<LoanValidationResult>(url));

      if (!result.loanNumber) {
        this.validationAttempts.update((count) => count + 1);
        this.error.set(result.displayErrorMessage || 'Loan not found');
      } else {
        this.validationAttempts.set(0);
      }

      return result;
    } catch (err) {
      this.loggingService.logException(err, 'Error validating loan:');
      this.validationAttempts.update((count) => count + 1);
      this.error.set('Unable to validate loan. Please try again.');
      throw err;
    } finally {
      this.isLoading.set(false);
    }
  }

  async registerLoan(globalUserId: string, loanDetails: LoanValidationResult): Promise<boolean> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      const url = `${this.baseUrl}/${this.apiNamespace}/LDProfileDomain/UserProfile/RegisterUserProperty?userId=${globalUserId}`;

      const properties: LoanProperty[] = [
        {
          key: 'LoanNumber',
          value: loanDetails.loanNumber || '',
          originalValue: loanDetails.loanNumber || '',
          collectionName: 'Loan',
        },
        {
          key: 'ZipCode',
          value: loanDetails.zipCode || '',
          originalValue: loanDetails.zipCode || '',
          collectionName: 'Loan',
        },
        {
          key: 'LastSSN',
          value: loanDetails.ssn || '',
          originalValue: loanDetails.ssn || '',
          collectionName: 'Loan',
        },
        {
          key: 'LoanType',
          value: loanDetails.loanType || 'Mortgage',
          originalValue: loanDetails.loanType || 'Mortgage',
          collectionName: 'Loan',
        },
      ];

      const result = await firstValueFrom(this.http.post<boolean>(url, properties));

      if (result) {
        sessionStorage.setItem('NEWLY_LINKED_LOAN_NO', loanDetails.loanNumber || '');
      }

      return result;
    } catch (err) {
      this.loggingService.logException(err, 'Error registering loan:');
      this.error.set('Unable to link loan. Please try again.');
      throw err;
    } finally {
      this.isLoading.set(false);
    }
  }

  hasExceededMaxAttempts(): boolean {
    return this.validationAttempts() >= 3;
  }

  reset(): void {
    this.validationAttempts.set(0);
    this.error.set(null);
    this.isLoading.set(false);
  }

  clearError(): void {
    this.error.set(null);
  }

  getNewlyLinkedLoanNumber(): string | null {
    return sessionStorage.getItem('NEWLY_LINKED_LOAN_NO');
  }

  clearNewlyLinkedLoanNumber(): void {
    sessionStorage.removeItem('NEWLY_LINKED_LOAN_NO');
  }
}
